import * as THREE from 'three'
import enemyArchetypesJson from '../../assets/data/enemy_archetypes.json'

export type EnemyArchetype =
  | 'patrol_guard'
  | 'melee_swarmer'
  | 'heavy_enforcer'
  | 'sniper_turret'
  | 'boss_overlord'
  | 'scout-drone'
  | 'cyborg-trooper'
  | 'riot-trooper'
  | 'defense-turret'

export interface LootEntry {
  item: string
  weight: number
}

export interface EnemyArchetypeDefinition {
  id: EnemyArchetype
  name: string
  HP: number
  maxHp: number
  speed: number
  moveSpeed: number
  detectionRange: number
  attackRange: number
  attackCooldown: number
  fireRate: number // attacks per sec
  attackDamage: number
  projectileDamage: number
  projectileSpeed: number
  projectileType: string
  shieldCapacity: number
  hasRiotShield: boolean
  isFlying: boolean
  isTurret: boolean
  color: number
  size: [number, number, number]
  lootTable?: LootEntry[]
}

export const ENEMY_ARCHETYPES: Record<EnemyArchetype, EnemyArchetypeDefinition> =
  enemyArchetypesJson as unknown as Record<EnemyArchetype, EnemyArchetypeDefinition>

/**
 * Checks if incoming damage is deflected by a riot shield.
 * Frontal angle threshold: dot product between facing and opposite-projectile-dir > 0.3.
 * Explosive AOE bypasses the shield completely.
 */
export function isDamageBlockedByShield(
  enemyFacing: THREE.Vector3,
  projectileVelocityDir: THREE.Vector3,
  isExplosive: boolean,
): boolean {
  if (isExplosive) return false

  // Normalize vectors horizontally
  const fX = enemyFacing.x
  const fZ = enemyFacing.z
  const fLen = Math.hypot(fX, fZ)
  if (fLen < 0.0001) return false

  const pX = projectileVelocityDir.x
  const pZ = projectileVelocityDir.z
  const pLen = Math.hypot(pX, pZ)
  if (pLen < 0.0001) return false

  // Dot product between enemy facing and vector facing back against projectile travel
  const dot = -(fX * pX + fZ * pZ) / (fLen * pLen)
  return dot > 0.3
}

/**
 * Calculates yaw and pitch angles for automated turrets tracking a target.
 */
export function calculateTurretAim(
  turretPos: THREE.Vector3,
  targetPos: THREE.Vector3,
): { yaw: number; pitch: number } {
  const dx = targetPos.x - turretPos.x
  const dy = targetPos.y - turretPos.y
  const dz = targetPos.z - turretPos.z

  const horizontalDist = Math.hypot(dx, dz)
  const yaw = Math.atan2(dx, dz)
  const pitch = Math.atan2(dy, horizontalDist)

  return { yaw, pitch }
}
